/**
 * In-app help: the "How this works" link and side panel (a full-screen sheet on a phone), the Help screen
 * (#/help and #/help/<page>: searchable guides, reference documents, reports), "Something's not working",
 * the What's new card on Today, and the one-line hint in empty lists.
 *
 * Crew users see crew and all pages; office users see every page. Wording comes from the client layer.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent, type MouseEvent, type ReactNode } from "react";
import { renderHelpMarkdown } from "@ops/client-config/markdown";
import { Icon } from "../../components/Icon";
import { currentRoute, isDirector, listeners, openReport, reportsOn, viewer, HowThisWorks, type PanelState } from "./help-links";
import { Notice, useNotice } from "../../components/Notice";
import { FilterBar, PageHeader, SearchField } from "../../components/PageHeader";
import { Chip, EmptyState, ErrorState, SkeletonLines } from "../../components/States";
import { BRAND } from "../../lib/config";
import { fmtDate, fmtPlainDate } from "../../lib/format";
import { openBlobInNewTab } from "../../lib/jobs-data";
import { hashParams, useHash } from "../../lib/route";
import { useAsync } from "../../lib/useAsync";
import {
  canCaptureScreen,
  captureScreen,
  documentFile,
  entryKey,
  groupedPages,
  helpPage,
  listDocuments,
  listReports,
  markWhatsNewSeen,
  pageForRoute,
  relatedPages,
  removeDocument,
  replyToReport,
  REPORT_STATUS_LABEL,
  reportScreenshot,
  sendReport,
  unseenWhatsNew,
  uploadDocument,
  whatsNew,
  type HelpPage,
  type SupportReport,
  type Who,
} from "./help-data";

/* ------------------------------------------------------------------ the panel */

function PageBody({ page, onLink }: { page: HelpPage; onLink?: (id: string) => void }) {
  const html = useMemo(() => renderHelpMarkdown(page.body), [page.body]);
  const click = (e: MouseEvent<HTMLDivElement>) => {
    const a = (e.target as HTMLElement).closest("a");
    const href = a?.getAttribute("href") ?? "";
    if (onLink && href.startsWith("#/help/")) {
      e.preventDefault();
      onLink(href.slice(7));
    }
  };
  // renderHelpMarkdown escapes everything it does not produce itself.
  return <div className="helpdoc" onClick={click} dangerouslySetInnerHTML={{ __html: html }} />;
}

/** Mounted once by the app shell: the help panel and the report form. */
export function HelpHost() {
  const [state, setState] = useState<PanelState>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const l = (s: PanelState) => {
      if (s && !returnFocus.current) returnFocus.current = document.activeElement as HTMLElement | null;
      setState(s);
    };
    listeners.add(l);
    return () => void listeners.delete(l);
  }, []);
  useEffect(() => {
    if (!state) {
      returnFocus.current?.focus?.();
      returnFocus.current = null;
      return;
    }
    panelRef.current?.querySelector<HTMLElement>("h2")?.focus();
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setState(null);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [state]);
  useEffect(() => {
    const close = () => setState(null);
    window.addEventListener("hashchange", close);
    return () => window.removeEventListener("hashchange", close);
  }, []);
  if (!state) return null;
  const page = state.kind === "page" ? helpPage(state.id, viewer.who) : undefined;
  return (
    <>
      <div className="helpscrim" aria-hidden="true" onClick={() => setState(null)} />
      <div ref={panelRef} className="helppanel" role="dialog" aria-modal="true" aria-labelledby="helppanel-title">
        <div className="helppanel__head">
          <h2 id="helppanel-title" tabIndex={-1}>{state.kind === "report" ? "Something's not working" : (page?.title ?? "Help")}</h2>
          <button type="button" className="icon-btn" aria-label="Close help" onClick={() => setState(null)}><Icon name="close" size={22} /></button>
        </div>
        <div className="helppanel__body">
          {state.kind === "report" ? (
            <ReportForm onDone={() => setState(null)} />
          ) : page ? (
            <>
              <p className="muted">{page.summary}</p>
              <PageBody page={page} onLink={(id) => setState({ kind: "page", id })} />
              {relatedPages(page, viewer.who).length > 0 && (
                <>
                  <h3 className="helppanel__sub">Related</h3>
                  <ul className="helplinks">
                    {relatedPages(page, viewer.who).map((p) => (
                      <li key={p.id}><button type="button" className="linkbtn" onClick={() => setState({ kind: "page", id: p.id })}>{p.title}</button></li>
                    ))}
                  </ul>
                </>
              )}
              <div className="form-actions helppanel__foot">
                <a className="btn btn--outline btn--sm" href={`#/help/${page.id}`}>Open in Help</a>
                {reportsOn() && <button type="button" className="btn btn--ink btn--sm" onClick={() => setState({ kind: "report" })}>Something's not working</button>}
              </div>
            </>
          ) : (
            <p>That guide is not available.</p>
          )}
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ Something's not working */

function ReportForm({ onDone }: { onDone: () => void }) {
  const route = useRef(window.location.hash || currentRoute());
  const [trying, setTrying] = useState("");
  const [happened, setHappened] = useState("");
  const [shot, setShot] = useState<Blob | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const { notice, setNotice, fail } = useNotice();
  useEffect(() => {
    if (!shot) return setPreview(null);
    const u = URL.createObjectURL(shot);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [shot]);

  async function take() {
    setNotice(null);
    const b = await captureScreen();
    if (b) setShot(b);
    else setNotice({ tone: "error", text: "No screenshot was taken. You can attach a picture instead." });
  }
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      const r = await sendReport({ trying: trying.trim(), happened: happened.trim(), route: route.current, screenshot: shot });
      setDone(r.message);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }
  if (done) {
    return (
      <div className="stack">
        <p className="notice notice--ok" role="status"><Icon name="check" size={18} />{done}</p>
        <p className="small muted">Any reply shows under Your reports in Help.</p>
        <div className="form-actions"><button type="button" className="btn btn--ink btn--sm" onClick={onDone}>Close</button></div>
      </div>
    );
  }
  return (
    <form className="form" onSubmit={(e) => void submit(e)}>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <label className="field">
        <span className="field__label">What were you trying to do?</span>
        <textarea className="input" rows={3} required minLength={3} maxLength={4000} value={trying} onChange={(e) => setTrying(e.target.value)} />
      </label>
      <label className="field">
        <span className="field__label">What happened?</span>
        <textarea className="input" rows={3} maxLength={4000} value={happened} onChange={(e) => setHappened(e.target.value)} />
        <span className="field__hint">Optional</span>
      </label>
      <div className="field">
        <span className="field__label">Screenshot</span>
        <div className="form-actions">
          {canCaptureScreen() && <button type="button" className="btn btn--outline btn--sm" onClick={() => void take()}>Take a screenshot of this screen</button>}
          <label className="btn btn--outline btn--sm">
            Attach a picture
            <input className="sr-only" type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setShot(e.target.files?.[0] ?? null)} />
          </label>
        </div>
        {preview && (
          <div className="helpshot">
            <img src={preview} alt="The screenshot that will be sent" />
            <button type="button" className="linkbtn" onClick={() => setShot(null)}>Remove</button>
          </div>
        )}
        <span className="field__hint">Your name, this screen, the app version, your browser and any recent errors are added for you.</span>
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn--primary btn--sm" disabled={busy} aria-busy={busy}>Send report</button>
        <button type="button" className="btn btn--outline btn--sm" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

/** For the user menu. */
export function ReportButton({ className, label = "Something's not working" }: { className?: string; label?: string }) {
  if (!reportsOn()) return null;
  return (
    <button type="button" className={className ?? "btn btn--outline btn--sm"} onClick={openReport}>
      <Icon name="warning" size={20} />
      <span className="side__btn-label">{label}</span>
    </button>
  );
}

/* ------------------------------------------------------------------ the Help screen */

export function HelpScreen() {
  const [id] = hashParams(useHash());
  if (id) return <HelpPageView key={id} id={id} />;
  return <HelpHome />;
}

function HelpHome() {
  const [q, setQ] = useState("");
  const groups = groupedPages(viewer.who, q);
  const count = groups.reduce((n, g) => n + g.pages.length, 0);
  return (
    <section className="page" aria-labelledby="page-title">
      <PageHeader help={false} eyebrow={BRAND.name} title="Help" meta="Guides for each screen, reference documents, and a way to tell us when something is not working." actions={<ReportButton />} />
      <FilterBar count={q ? `${count} ${count === 1 ? "guide" : "guides"}` : undefined}>
        <SearchField label="Search help" placeholder="Search the guides" value={q} onChange={setQ} />
      </FilterBar>
      {!count && <EmptyState help={false} icon="inbox" title={BRAND.help.pages.length ? "No guides match" : "No guides yet"}>{BRAND.help.pages.length ? "Try other words." : "Guides appear here when they are added."}</EmptyState>}
      <div className="stack">
        {groups.map((g) => (
          <div key={g.area} className="card">
            <div className="card__head"><h2 className="card__title">{g.area}</h2></div>
            <ul className="list">
              {g.pages.map((p) => (
                <li key={p.id} className="list__row"><span><a className="strong" href={`#/help/${p.id}`}>{p.title}</a><span className="sub">{p.summary}</span></span></li>
              ))}
            </ul>
          </div>
        ))}
        <Documents />
        <Reports />
      </div>
    </section>
  );
}

function HelpPageView({ id }: { id: string }) {
  const page = helpPage(id, viewer.who);
  if (!page) return <section className="page"><EmptyState help={false} icon="warning" title="Guide not found"><a href="#/help">All guides</a></EmptyState></section>;
  const related = relatedPages(page, viewer.who);
  const isWhatsNew = page.id === "whats-new";
  return (
    <section className="page" aria-labelledby="page-title">
      <p className="small" style={{ margin: "0 0 8px" }}><a href="#/help">All guides</a></p>
      <PageHeader help={false} eyebrow="Help" title={page.title} meta={page.summary} actions={page.route.startsWith("#/") && page.route !== "#/help" && !isWhatsNew ? <a className="btn btn--outline btn--sm" href={viewer.who === "crew" ? "#/" : page.route}>Go to {page.route === "#/my" ? "your shifts" : "the screen"}</a> : undefined} />
      <div className="card"><div className="card__body"><PageBody page={page} onLink={(p) => (window.location.hash = `#/help/${p}`)} /></div></div>
      <p className="small muted">Updated {fmtPlainDate(page.updated)}{page.platform ? `, for ${page.platform}` : ""}.</p>
      {related.length > 0 && (
        <div className="card">
          <div className="card__head"><h2 className="card__title">Related</h2></div>
          <ul className="list">{related.map((p) => <li key={p.id} className="list__row"><span><a className="strong" href={`#/help/${p.id}`}>{p.title}</a><span className="sub">{p.summary}</span></span></li>)}</ul>
        </div>
      )}
      {reportsOn() && <div className="form-actions"><ReportButton /></div>}
    </section>
  );
}

const AUDIENCE_LABEL: Record<string, string> = { office: "Office", crew: "Site", all: "Everyone" };

function Documents() {
  const s = useAsync(listDocuments);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  const docs = s.data ?? [];
  if (!isDirector() && !s.loading && !docs.length) return null;
  async function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const file = f.get("file");
    if (!(file instanceof File) || !file.size) return;
    setBusy(true);
    setNotice(null);
    try {
      await uploadDocument(String(f.get("title") ?? ""), String(f.get("audience") ?? "all"), file);
      ok("Document added.");
      setAdding(false);
      s.reload();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="card">
      <div className="card__head">
        <h2 className="card__title">Reference documents</h2>
        {isDirector() && !adding && <button type="button" className="btn btn--outline btn--sm" onClick={() => setAdding(true)}><Icon name="plus" size={16} /> Add document</button>}
      </div>
      <div className="card__body stack">
        <Notice notice={notice} onDismiss={() => setNotice(null)} />
        {s.loading && !s.data && <SkeletonLines rows={2} />}
        {s.error && <ErrorState message={s.error} onRetry={s.reload} />}
        {adding && (
          <form className="form-grid" onSubmit={(e) => void add(e)}>
            <label className="field"><span className="field__label">Title</span><input className="input" name="title" required maxLength={200} placeholder="Office guide" /></label>
            <label className="field"><span className="field__label">Who sees it</span>
              <select className="input" name="audience" defaultValue="all"><option value="all">Everyone</option><option value="office">Office only</option><option value="crew">Site staff</option></select>
            </label>
            <label className="field field--wide"><span className="field__label">File</span><input className="input" name="file" type="file" required accept=".pdf,.docx,.xlsx,image/png,image/jpeg" /></label>
            <div className="form-actions field--wide">
              <button type="submit" className="btn btn--ink btn--sm" disabled={busy} aria-busy={busy}>Upload</button>
              <button type="button" className="btn btn--outline btn--sm" onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </form>
        )}
        {s.data && !docs.length && !adding && <p className="small muted">No documents yet. Add guides, workbooks and templates people need to hand.</p>}
        {docs.length > 0 && (
          <ul className="list">
            {docs.map((d) => (
              <li key={d.id} className="list__row">
                <span><button type="button" className="linkbtn strong" onClick={() => void openBlobInNewTab(() => documentFile(d.id))}>{d.title}</button><span className="sub">{d.file_name}{viewer.who === "office" ? `, ${AUDIENCE_LABEL[d.audience]}` : ""}</span></span>
                {isDirector() && (
                  <button type="button" className="btn btn--outline btn--xs" onClick={() => void removeDocument(d.id).then(() => { ok("Document removed."); s.reload(); }).catch(fail)}>Remove</button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const STATUS_TONE = { open: "primary", answered: "accent", fixed: "green" } as const;

function Reports() {
  const s = useAsync(listReports);
  const reports = s.data ?? [];
  if (!reportsOn() || (!reports.length && !s.error)) return null;
  return (
    <div className="card">
      <div className="card__head"><h2 className="card__title">{isDirector() ? "Problem reports" : "Your reports"}</h2></div>
      <div className="card__body stack">
        {s.error && <ErrorState message={s.error} onRetry={s.reload} />}
        {reports.map((r) => <ReportRow key={r.id} r={r} onChange={s.reload} />)}
      </div>
    </div>
  );
}

function ReportRow({ r, onChange }: { r: SupportReport; onChange: () => void }) {
  const [status, setStatus] = useState(r.status);
  const [reply, setReply] = useState(r.reply ?? "");
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  return (
    <div className="helpreport">
      <p className="strong" style={{ margin: 0 }}>{r.trying} <Chip tone={STATUS_TONE[r.status]}>{REPORT_STATUS_LABEL[r.status]}</Chip></p>
      <p className="small muted" style={{ margin: "2px 0 6px" }}>{fmtDate(r.created_at)}{isDirector() ? `, ${r.reporter_name ?? r.reporter_email ?? "user"} (${r.role}) on ${r.route ?? "an unknown screen"}` : ""}</p>
      {r.happened && <p className="small">{r.happened}</p>}
      {r.screenshot_key && <button type="button" className="linkbtn small" onClick={() => void openBlobInNewTab(() => reportScreenshot(r.id))}>View screenshot</button>}
      {isDirector() && r.api_errors.length > 0 && <ul className="small muted">{r.api_errors.map((e, i) => <li key={i}>{e.status} {e.path}: {e.message}</li>)}</ul>}
      {r.reply && !isDirector() && <p className="notice notice--ok small" role="status">Reply: {r.reply}</p>}
      {isDirector() && (
        <form
          className="form-grid"
          onSubmit={(e) => {
            e.preventDefault();
            setBusy(true);
            setNotice(null);
            replyToReport(r.id, status, reply.trim() || null)
              .then(() => {
                ok("Saved. The person who reported it sees the reply in Help.");
                onChange();
              })
              .catch(fail)
              .finally(() => setBusy(false));
          }}
        >
          <div className="field--wide"><Notice notice={notice} onDismiss={() => setNotice(null)} /></div>
          <label className="field"><span className="field__label">Status</span>
            <select className="input" value={status} onChange={(e) => setStatus(e.target.value as SupportReport["status"])}>
              <option value="open">Open</option><option value="answered">Answered</option><option value="fixed">Fixed</option>
            </select>
          </label>
          <label className="field field--wide"><span className="field__label">Reply</span><textarea className="input" rows={2} maxLength={4000} value={reply} onChange={(e) => setReply(e.target.value)} /></label>
          <div className="form-actions field--wide"><button type="submit" className="btn btn--ink btn--xs" disabled={busy} aria-busy={busy}>Save</button></div>
        </form>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ What's new on Today */

export function WhatsNewCard() {
  const s = useAsync(unseenWhatsNew);
  const [hidden, setHidden] = useState(false);
  const entries = s.data ?? [];
  if (hidden || !entries.length) return null;
  const newest = entries[0]!;
  const dismiss = () => {
    setHidden(true);
    void markWhatsNewSeen(entryKey(whatsNew()[0]!)).catch(() => undefined);
  };
  return (
    <div className="card whatsnew" role="region" aria-labelledby="whatsnew-title">
      <div className="card__head">
        <h2 className="card__title" id="whatsnew-title">What's new: {newest.title}</h2>
        <button type="button" className="icon-btn" aria-label="Dismiss What's new" onClick={dismiss}><Icon name="close" size={20} /></button>
      </div>
      <div className="card__body">
        <PageBody page={{ id: "whats-new-entry", title: newest.title, route: "#/today", audience: "all", summary: "", updated: newest.date, body: newest.body }} />
        <div className="form-actions">
          <button type="button" className="btn btn--ink btn--sm" onClick={dismiss}>Got it</button>
          {entries.length > 1 && <a className="btn btn--outline btn--sm" href="#/help/whats-new">{entries.length - 1} earlier {entries.length === 2 ? "update" : "updates"}</a>}
        </div>
      </div>
    </div>
  );
}

/** A small "How this works" link for a section of a screen (e.g. checklists in the job pack). */
export function SectionHelp({ pageId, children }: { pageId: string; children?: ReactNode }) {
  if (!helpPage(pageId, viewer.who)) return null;
  return <HowThisWorks pageId={pageId} compact label={(children as string) ?? "How this works"} />;
}
